import PropTypes from 'prop-types'

// material-ui
import { useTheme } from '@mui/material/styles'
import { Stack, Typography, useMediaQuery, IconButton } from '@mui/material'
import { LayoutOutlined } from '@ant-design/icons'

// project import
import DrawerHeaderStyled from './DrawerHeaderStyled'
import Logo from 'components/logo'

import { MenuOrientation } from 'config'
import useConfig from 'hooks/useConfig'
import { useMemo } from 'react'
import useAuth from 'hooks/useAuth'

// ==============================|| DRAWER HEADER ||============================== //

const createStyles = (theme) => ({
  logoContainer: {
    '&[data-open="false"]': {
      mt: '12px',
      mx: '6px',
    },
  },
  subLogo: {
    color: '#D2D7DF',
    display: 'flex',
    alignItems: 'center',
    '&[data-open="true"]': {
      gap: '12px',
    },
    '&[data-open="false"]': {
      gap: '6px',
      mr: '6px',
    },
  },
  logoContainerHover: {
    '&:hover a': {
      display: 'none',
    },
    '& button': {
      display: 'none',
    },
    '&:hover button': {
      display: 'block',
    },
  },
})

const DrawerHeader = ({ open, onToggle }) => {
  const theme = useTheme()
  const styles = createStyles(theme)
  const downLG = useMediaQuery(theme.breakpoints.down('lg'))
  const { user } = useAuth()

  const { menuOrientation } = useConfig()
  const isHorizontal = menuOrientation === MenuOrientation.HORIZONTAL && !downLG

  const tenantLogo = useMemo(() => user?.organizationLogo, [user?.organizationLogo])

  return (
    <DrawerHeaderStyled
      theme={theme}
      open={open}
      sx={{
        minHeight: isHorizontal ? 'unset' : 'auto',
        width: isHorizontal ? { xs: '100%', lg: '424px' } : 'inherit',
        padding: '12px',
      }}
    >
      {tenantLogo ? (
        <Stack sx={styles.logoContainer} gap={1.5} data-open={open} width="100%">
          <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
            <img
              src={tenantLogo}
              alt="tenant's logo"
              width={open ? 69 : 52}
              height={open ? 24 : 18}
              style={{ objectFit: 'contain' }}
            />
            {open && (
              <IconButton onClick={onToggle}>
                <LayoutOutlined style={{ fontSize: '0.75rem' }} />
              </IconButton>
            )}
          </Stack>
          <Stack
            direction={open ? 'column' : 'row'}
            justifyContent={'space-around'}
            alignContent={open ? 'flex-start' : 'center'}
          >
            <Typography
              variant={'caption'}
              sx={styles.subLogo}
              data-open={open}
              fontSize={!open && '0.875rem'}
              color={open ? theme.palette.text.disabled : theme.palette.text.secondary}
              fontWeight={open ? 500 : 400}
            >
              {open ? 'Powered by ' : 'by '}
            </Typography>
            <Stack sx={!open ? styles.logoContainerHover : undefined}>
              <Logo
                isIcon={!open}
                sx={{ width: open ? '130px' : 21, height: 21, justifyContent: 'flex-start' }}
              />
              {!open && (
                <IconButton
                  onClick={onToggle}
                  size="small"
                  sx={{ width: 22, height: 22, p: '3px' }}
                >
                  <LayoutOutlined style={{ fontSize: '1rem' }} />
                </IconButton>
              )}
            </Stack>
          </Stack>
        </Stack>
      ) : (
        <>
          {open ? (
            <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
              <Logo sx={{ width: 'auto', height: 35 }} />
              <IconButton onClick={onToggle}>
                <LayoutOutlined style={{ fontSize: '0.75rem' }} />
              </IconButton>
            </Stack>
          ) : (
            <Stack sx={styles.logoContainerHover}>
              <Logo isIcon sx={{ width: 35, height: 36 }} />
              <IconButton onClick={onToggle}>
                <LayoutOutlined style={{ fontSize: '1rem' }} />
              </IconButton>
            </Stack>
          )}
        </>
      )}
    </DrawerHeaderStyled>
  )
}

DrawerHeader.propTypes = {
  open: PropTypes.bool,
}

export default DrawerHeader
